import { useQueries } from '@tanstack/react-query'
import { useMemo, useState } from 'react'

import { fetchServiceSession, serviceSessionKey } from '@/api/cafeteria'
import type { RosterFilter } from '@/lib/cafeteria/print-plan'
import type { Mealtime } from '@/lib/mealtime'
import {
  ALL_ROSTER_GROUPS,
  changeRosterSelection,
  type RosterSelection,
  rosterSelectionOptions,
  selectedRosterLabels,
} from '@/lib/school-print/roster-selection'

export function useSchoolPrintRoster({
  locationId,
  axis,
  sessions,
  enabled,
}: {
  locationId: string | null
  axis: RosterFilter['axis']
  sessions: readonly { serviceDate: string; mealtime: Mealtime }[]
  enabled: boolean
}) {
  const context = `${locationId}|${axis}`
  const [pick, setPick] = useState<{ context: string; selection: RosterSelection } | null>(null)
  const selection = pick?.context === context ? pick.selection : ALL_ROSTER_GROUPS
  const details = useQueries({
    queries: (enabled && locationId ? sessions : []).map(({ serviceDate, mealtime }) => {
      const args = { schoolLocationId: locationId!, serviceDate, mealtime }
      return {
        queryKey: serviceSessionKey(args),
        queryFn: ({ signal }: { signal?: AbortSignal }) => fetchServiceSession(args, signal),
        staleTime: 10 * 1000,
      }
    }),
  })
  const options = rosterSelectionOptions(
    details.flatMap((detail) => (detail.data ? [detail.data] : [])),
    axis,
  )
  const labels = selectedRosterLabels(options, selection)
  const pending = details.some((detail) => detail.isPending)
  const error = details.some((detail) => detail.isError)
  const groupingDisabled = labels.size <= 1
  const grouped = groupingDisabled || selection.grouped
  const filter = useMemo<RosterFilter | undefined>(
    () => (selection.labels === null ? undefined : { axis, labels: selection.labels }),
    [axis, selection.labels],
  )

  function selectLabels(next: ReadonlySet<string>): void {
    setPick({ context, selection: changeRosterSelection(options, selection, next) })
  }

  function setGrouped(next: boolean): void {
    setPick({ context, selection: { ...selection, grouped: groupingDisabled || next } })
  }

  return { options, labels, selectLabels, grouped, setGrouped, groupingDisabled, pending, error, filter }
}
